import { IconArrowInbox } from 'central-icons/IconArrowInbox';
import { useMemo } from 'react';

import { useUserTrialDetails } from '@revelio/auth';
import type { TalentDiscoveryReqV3 } from '@revelio/data-access';
import { MenuItem } from '@revelio/ui';

import { useDownloadData } from '../../../../shared/hooks/useDownloadPlotData';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { buildTalentDiscoveryFiltersV3 } from '../DataProvider/talentDiscovery/adapter';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../PageConfigProvider/types';

export const TalentDiscoveryDataExportMenuItem = () => {
  const { revelioPageId } = useTabContentConfig();
  const { isTrialUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });
  const filters = useEffectiveFilters();

  const serialisedFilters: TalentDiscoveryReqV3 | null = useMemo(() => {
    if (revelioPageId !== RevelioPageId.TALENT_DISCOVERY) {
      return null;
    }

    const v3Query = buildTalentDiscoveryFiltersV3(filters);
    // Override page_size for downloads
    const v3QueryWithLimit = {
      ...v3Query,
      pagination: {
        ...v3Query.pagination,
        page_size: 10000,
      },
    };

    // Validate v3 filters by checking expr structure
    const expr = v3QueryWithLimit.filters.expr;
    if (!expr) {
      return null;
    }

    if (expr.user_and) {
      return expr.user_and.children.length > 0 ? v3QueryWithLimit : null;
    }

    if (expr.user_or) {
      return expr.user_or.children.length > 0 ? v3QueryWithLimit : null;
    }

    if (expr.user_value) {
      return v3QueryWithLimit;
    }

    if (expr.user_not) {
      return v3QueryWithLimit;
    }

    return null;
  }, [filters, revelioPageId]);

  const downloadPath = 'api/talent-discovery/v3/downloads';

  const { triggerDownload, isLoading } =
    useDownloadData<TalentDiscoveryReqV3 | null>({
      serialisedFilters,
      downloadPath,
    });

  const isDisabled = isTrialUser || !serialisedFilters || isLoading;

  return (
    <MenuItem
      icon={<IconArrowInbox size={16} />}
      onClick={triggerDownload}
      disabled={isDisabled}
    >
      Download Data
    </MenuItem>
  );
};
